import React, { useContext, useEffect, useState, useRef, type ReactPortal } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { useWorkspace } from '../../../context/WorkspaceContext'
import { RemoteConfigContext } from '../../../context/RemoteConfigContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { Card } from './card'

import { SwitchCalendarDialog } from './switchCalendarDialog'
import { GongImportDialog } from './gongImportDialog'
import { createPortal } from 'react-dom'
import { GoogleIcon } from '../../icons/Google'
import { MicrosoftIcon } from '../../icons/Microsoft'
import { ZoomIcon } from '../../icons/Zoom'
import { SlackIcon } from '../../icons/Slack'
import { GongIcon } from '../../icons/Gong'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  IntegrationKeys,
  IntegrationKeysMap,
  IntegrationKey,
  personalIntegrations,
} from '../../../constants/integrations'

interface IntegrationsModalProps {
  open: boolean
  onClose: () => void
}

export function IntegrationsModal({ open, onClose }: IntegrationsModalProps) {
  const { user, setUser } = useContext(AuthContext)
  const { config } = useContext(RemoteConfigContext)
  const { workspace: workspaceData, isPartner, refreshWorkspace } = useWorkspace()

  /**
   * Organization-wide integrations that apply to the entire workspace
   * These are managed at the workspace level rather than user level
   */
  const organizationWideIntegrations: IntegrationKey[] = []
  if (config.slack) organizationWideIntegrations.push(IntegrationKeys.Slack)
  // Disable Gong temporarily
  // if (config.gong) organizationWideIntegrations.push(IntegrationKeys.Gong)

  // Controls which calendar switch dialog is shown ('googleCalendar', 'microsoftCalendar', or empty string)
  const [showSwitchDialog, setShowSwitchDialog] = useState('')

  // Controls visibility of the Gong import dialog
  const [showGongImportDialog, setShowGongImportDialog] = useState(false)

  // Loading state for the entire component
  const [isLoading, setIsLoading] = useState(true)

  // Tracks which integrations are currently in the process of connecting/disconnecting
  const [isFetchingStatus, setIsFetchingStatus] = useState({})

  // Prevents duplicate processing of OAuth codes
  const processedCodes = useRef(new Set())

  // Prevents showing multiple toasts for the same operation
  const toastShown = useRef(false)

  const [saving, setSaving] = useState(false)

  // Centralized state for tracking connection status of all integrations
  const [integrationStatus, setIntegrationStatus] = useState<Record<string, boolean>>({})

  const customFetch = useCustomFetch()
  const signInMethod = localStorage.getItem('signin_method')

  // All the same logic from the original Integrations component
  const loadIntegrationStatus = async () => {
    try {
      setIsLoading(true)

      const res = await customFetch('/auth/getIntegrationStatus', 'GET', null)
      if (res && !res.error && res.status) {
        // map backend keys to frontend keys for status map (don't update global user state)
        const newStatus = { ...integrationStatus }
        Object.keys(res.status).forEach((k) => {
          const frontKey = IntegrationKeysMap[k]
          if (frontKey) newStatus[frontKey] = res.status[k]
        })
        setIntegrationStatus(newStatus)
      }
    } catch (err) {
      console.error('Error loading integration status', err)
    } finally {
      setIsLoading(false)
    }
  }

  const prepareHandleConnect = (integrationKey: string, showDialog: boolean = true) => {
    if (integrationKey === IntegrationKeys.Zoom) return zoomSignIn()
    if (integrationKey === IntegrationKeys.Slack) return slackSignIn()
    if (integrationKey === IntegrationKeys.Gong) return gongSignIn()
    if (showDialog) {
      if (integrationKey === IntegrationKeys.GoogleCalendar && integrationStatus.MicrosoftCalendar)
        return setShowSwitchDialog(integrationKey)
      if (integrationKey === IntegrationKeys.MicrosoftCalendar && integrationStatus.GoogleCalendar)
        return setShowSwitchDialog(integrationKey)
    } else {
      setShowSwitchDialog('')
    }
    if (integrationKey === IntegrationKeys.GoogleCalendar) {
      if (signInMethod === 'google') return handleConnect(IntegrationKeys.GoogleCalendar, null)
      else return googleCalendarSignIn()
    }
    if (integrationKey === IntegrationKeys.MicrosoftCalendar) {
      if (signInMethod === 'microsoft') return handleConnect(IntegrationKeys.MicrosoftCalendar, null)
      else return microsoftCalendarSignIn()
    }
  }

  const zoomSignIn = async () => {
    try {
      const response = await customFetch('/auth/getZoomUrl', 'POST', {})
      if (response.error) throw new Error(response.error)
      if (response.url) {
        // Store that we're coming from onboarding modal
        localStorage.setItem('oauth_from_onboarding', 'true')
        localStorage.setItem('oauth_integration_type', 'zoom')

        // Add state=zoom parameter to the URL
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'zoom')
        window.location.href = redirectUrl.toString()
      }
    } catch (error) {
      console.error('Error searching zoom:', error)
      toast.error('Error connecting')
    }
  }

  const slackSignIn = async () => {
    try {
      const response = await customFetch('/auth/getSlackUrl', 'POST', {})
      if (response.error) throw new Error(response.error)
      if (response.url) {
        // Store that we're coming from onboarding modal
        localStorage.setItem('oauth_from_onboarding', 'true')
        localStorage.setItem('oauth_integration_type', 'slack')

        // Add state=slack parameter to the URL
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'slack')
        window.location.href = redirectUrl.toString()
      }
    } catch (error) {
      console.error('Error searching slack:', error)
      toast.error('Error connecting')
    }
  }

  const googleCalendarSignIn = async () => {
    ;(window as any).google.accounts.oauth2
      .initCodeClient({
        client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
        scope:
          'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/calendar.readonly',
        ux_mode: 'popup',
        callback: async (response) => handleConnect(IntegrationKeys.GoogleCalendar, response.code),
      })
      .requestCode()
  }

  const microsoftCalendarSignIn = async () => {
    // Store that we're coming from onboarding modal
    localStorage.setItem('oauth_from_onboarding', 'true')
    localStorage.setItem('oauth_integration_type', 'microsoftCalendar')

    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const redirectUri = encodeURIComponent(import.meta.env.VITE_CLIENT_URL + '/dashboard/integrations')
    const state = encodeURIComponent(IntegrationKeys.MicrosoftCalendar)
    const scopes = encodeURIComponent('User.Read Calendars.Read offline_access')
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scopes}&response_mode=query&state=${state}`
  }

  const gongSignIn = async () => {
    try {
      // Reset toast shown flag
      toastShown.current = false

      // Get the Gong authorization URL using the new Gong API route
      const response = await customFetch('/gong/getAuthUrl', 'POST', {})

      if (response.error) throw new Error(response.error)

      if (response.url) {
        // Store that we're coming from onboarding modal
        localStorage.setItem('oauth_from_onboarding', 'true')
        localStorage.setItem('oauth_integration_type', 'gong')

        // Add state=gong parameter to identify this as a Gong redirect
        const redirectUrl = new URL(response.url)
        redirectUrl.searchParams.append('state', 'gong')

        // Clear any existing flags
        localStorage.removeItem('show_gong_import')
        localStorage.removeItem('gong_redirect_pending')

        // Set a flag to indicate we're expecting a Gong redirect
        localStorage.setItem('gong_redirect_pending', 'true')

        // Redirect to Gong authorization
        window.location.href = redirectUrl.toString()
      }
    } catch (error) {
      console.error('Error connecting to Gong:', error)
      toast.error('Error connecting to Gong')
    }
  }

  const handleConnect = async (key: IntegrationKey, code: string) => {
    console.log(`handleConnect - key: ${key}, code: ${code}`)

    try {
      setSaving(true)

      try {
        const response = await customFetch('/auth/connectIntegration', 'POST', { code, key })
        if (response && !response.error) {
          console.log(`handleConnect - response: ${JSON.stringify(response, null, 2)}`)

          // Refresh workspace if it's an organization-wide integration
          if (organizationWideIntegrations.includes(key)) {
            refreshWorkspace(true)
          }

          // Update local integration status
          setIntegrationStatus((prev) => ({ ...prev, [IntegrationKeysMap[key]]: true }))

          toast.success(`${getName(key)} successfully connected!`)
        } else {
          console.error(`handleConnect - error: ${response.error}`)
          toast.error(`Error connecting ${getName(key)}: ${response.error}`)
        }
      } catch (networkError) {
        console.error('Network error connecting integration:', networkError)
        toast.error(`Network error connecting ${getName(key)}`)
      }
    } catch (error) {
      console.error('Error connecting integration:', error)
      toast.error(`Error connecting ${getName(key)}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async (key: string) => {
    try {
      return await disconnectIntegration(key)
    } catch (error) {
      console.error(`Error in handleDisconnect for ${key}:`, error)
      return false
    }
  }

  const disconnectIntegration = async (key: string) => {
    // Reset toast shown flag
    toastShown.current = false

    // Set fetching status to true for this integration
    setIsFetchingStatus((prev) => ({ ...prev, [key]: true }))

    const backendKey = IntegrationKeys[key]

    if (!backendKey) {
      console.error('Invalid integration key:', key)
      if (!toastShown.current) {
        toast.error(`Failed to disconnect: Invalid integration key`)
        toastShown.current = true
      }
      setIsFetchingStatus((prev) => ({ ...prev, [key]: false }))
      return false
    }

    try {
      // Make API call to disconnect the integration
      const response = await customFetch('/auth/disconnectIntegration', 'DELETE', { key: backendKey })

      if (response && response.error) {
        throw new Error(response.error)
      }

      // Update local state based on integration type
      if (organizationWideIntegrations.includes(backendKey)) {
        // Update workspace integrations
        refreshWorkspace(true)
      }
      // Note: Don't update global user state in modal to avoid triggering onboarding changes

      // Update the integration status
      setIntegrationStatus((prev) => ({ ...prev, [key]: false }))

      // Success message
      if (!toastShown.current) {
        toast.success(`${getName(backendKey)} disconnected successfully`)
        toastShown.current = true
      }

      return true
    } catch (error) {
      console.error(`Error disconnecting ${getName(backendKey)}:`, error)
      if (!toastShown.current) {
        toast.error(`Failed to disconnect ${getName(backendKey)}: ${error.message}`)
        toastShown.current = true
      }
      return false
    } finally {
      setIsFetchingStatus((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleGongImport = async (selectedFilters: any) => {
    setSaving(true)
    try {
      const response = await customFetch('/gong/import', 'POST', selectedFilters)

      if (response.ok) {
        toast.success('Gong import initiated successfully! Your meetings will be processed in the background.')
        setShowGongImportDialog(false)
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to import from Gong')
      }
    } catch (error) {
      console.error('Error importing from Gong:', error)
      toast.error('Failed to import from Gong')
    } finally {
      setSaving(false)
    }
  }

  const getIntegrationStatus = (integrationKey: string): boolean => {
    const shortKey = Object.keys(IntegrationKeys).find((k) => IntegrationKeys[k] === integrationKey)
    return shortKey ? integrationStatus[shortKey] : false
  }

  const getSortedIntegrations = (integrations: IntegrationKey[]) => {
    return integrations.sort((a, b) => {
      const aConnected = getIntegrationStatus(a)
      const bConnected = getIntegrationStatus(b)

      if (aConnected && !bConnected) return -1
      if (!aConnected && bConnected) return 1
      return 0
    })
  }

  // Strategy definitions - same as original
  const strategies = {
    [IntegrationKeys.GoogleCalendar]: {
      name: 'Google Calendar',
      description:
        'Connecting to Google Calendar allows the Evermuse bot to join meetings without being manually added.',
      getLogo: () => {
        return <GoogleIcon />
      },
    },
    [IntegrationKeys.MicrosoftCalendar]: {
      name: 'Microsoft Calendar',
      description:
        'Connecting to Microsoft Calendar allows the Evermuse bot to join meetings without being manually added.',
      getLogo: () => {
        return <MicrosoftIcon />
      },
    },
    [IntegrationKeys.Zoom]: {
      name: 'Zoom',
      description:
        'You must be connected to Zoom in order for the Evermuse bot to be able to join and analyze your meetings.',
      getLogo: () => {
        return <ZoomIcon />
      },
    },
    [IntegrationKeys.Slack]: {
      name: 'Slack',
      description:
        'You must be connected to Slack in order for the Evermuse bot to be able to join and analyze your channels.',
      getLogo: () => {
        return <SlackIcon />
      },
    },
    [IntegrationKeys.Gong]: {
      name: 'Gong.io',
      description: 'Connect to Gong to analyze your recorded meetings and get insights from your sales conversations.',
      getLogo: () => {
        return <GongIcon width="79" height="79" viewBox="0 0 93 100" />
      },
    },
  }

  const getName = (key: string) => {
    return strategies[key]?.name || ''
  }

  const getDescription = (key: string) => {
    return strategies[key]?.description || ''
  }

  const getLogo = (key: string) => {
    return strategies[key]?.getLogo() || null
  }

  // Load integration status on mount and handle OAuth success/error
  useEffect(() => {
    if (user && user.id && open) {
      loadIntegrationStatus()

      // Check for OAuth success/error from redirect
      const oauthSuccess = localStorage.getItem('oauth_success')
      const oauthSuccessType = localStorage.getItem('oauth_success_type')
      const oauthError = localStorage.getItem('oauth_error')
      const oauthErrorMessage = localStorage.getItem('oauth_error_message')

      if (oauthSuccess === 'true' && oauthSuccessType) {
        // Clear the flags
        localStorage.removeItem('oauth_success')
        localStorage.removeItem('oauth_success_type')

        // Show success message
        toast.success(`${getName(oauthSuccessType)} connected successfully!`)

        // Reload integration status to show the updated connection
        setTimeout(() => {
          loadIntegrationStatus()
        }, 1000)
      } else if (oauthError === 'true') {
        // Clear the flags
        localStorage.removeItem('oauth_error')
        localStorage.removeItem('oauth_error_message')

        // Show error message
        toast.error(oauthErrorMessage || 'Connection failed')
      }
    }
  }, [user?.id, open])

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl rounded-2xl p-0 flex flex-col max-h-[90vh]">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-xl font-semibold">Select Integrations</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <p className="text-[#40424A] text-base font-medium font-['Manrope']">Loading integrations...</p>
            ) : (
              <>
                <div className="mb-8">
                  <p className="mb-4 text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
                    For Myself
                  </p>
                  <div className="flex items-center justify-center gap-[19px] flex-wrap">
                    {/* Show all personal integrations (both connected and disconnected) with connected ones first */}
                    {getSortedIntegrations(personalIntegrations).map((key) => {
                      // Find the corresponding short key (G, M, Z, etc.) for this integration
                      const shortKey = Object.keys(IntegrationKeys).find((k) => IntegrationKeys[k] === key)

                      // Use the short key to check the integration status
                      const isConnected = shortKey ? integrationStatus[shortKey] : false

                      return (
                        <Card
                          key={key}
                          index={key}
                          connected={isConnected}
                          name={getName(key)}
                          description={getDescription(key)}
                          logo={getLogo(key)}
                          handleConnect={() => prepareHandleConnect(key, true)}
                          handleDisconnect={() => handleDisconnect(shortKey)}
                          handleImport={() => {}}
                        />
                      )
                    })}
                  </div>
                </div>

                {/* Only display the "For My Organization" section if the workspace is a partner */}
                {isPartner && (config.slack || config.gong) && (
                  <div className="mb-8">
                    <p className="mb-4 text-[#62708d] text-lg font-bold font-['Manrope'] leading-tight tracking-wide">
                      For My Organization
                    </p>
                    <div className="flex items-center justify-center gap-[19px] flex-wrap">
                      {getSortedIntegrations(organizationWideIntegrations).map((key) => {
                        // Find the corresponding short key for this integration
                        const shortKey = Object.keys(IntegrationKeys).find((k) => IntegrationKeys[k] === key)

                        // Use the short key to check the integration status
                        const isConnected = shortKey ? integrationStatus[shortKey] : false

                        return (
                          <Card
                            key={key}
                            index={key}
                            connected={isConnected}
                            name={getName(key)}
                            description={getDescription(key)}
                            logo={getLogo(key)}
                            handleConnect={() => prepareHandleConnect(key, true)}
                            handleDisconnect={() => handleDisconnect(shortKey)}
                            handleImport={key === IntegrationKeys.Gong ? () => setShowGongImportDialog(true) : () => {}}
                          />
                        )
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90" onClick={onClose}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      {showSwitchDialog
        ? (createPortal(
            <SwitchCalendarDialog
              handleClose={() => setShowSwitchDialog('')}
              handleSwitch={() => prepareHandleConnect(showSwitchDialog, false)}
            />,
            document.body
          ) as ReactPortal)
        : null}
      {showGongImportDialog
        ? (createPortal(
            <GongImportDialog handleClose={() => setShowGongImportDialog(false)} handleImport={handleGongImport} />,
            document.body
          ) as ReactPortal)
        : null}
    </>
  )
}
